import React, {
	useEffect,
	useState,
	useRef,
	isValidElement,
	cloneElement,
	Children,
	type JSX,
} from 'react'
import type {TabItem} from 'react-responsive-tabs'
import Tabs from 'react-responsive-tabs'
import {EllipseButton} from '@/products/flexible-web-toolkit/components/button/ellipse-button'
import {ButtonIcons} from '@/components/button-icons/button-icon'
import styles from './tabbed.content-block.module.css'

interface Color {
	defaultStyle?: string
	collapsed?: string
	selected?: string
}

interface TabProps {
	className?: string
	children: React.ReactNode
	icon?: React.ReactNode | null
	title: React.ReactNode
	disableOverflow?: boolean
	color?: {
		choice?: string
	}
}

const Tab = ({
	/* eslint-disable @typescript-eslint/no-unused-vars -- For markup */
	className = '',
	children,
	icon = null,
	title,
	disableOverflow,
	color = {},
	/* eslint-enable @typescript-eslint/no-unused-vars -- Enable rule */
}: TabProps): JSX.Element => {
	// This doesn't return anything, just used solely for passing in props etc in the markup
	return <> </>
}

interface TabbedContentBlockProps {
	className?: string
	children: React.ReactElement<TabProps>[]
	populateOverviewTab?: boolean
	overviewTabTitle?: string
	onChange?: (index: number | string) => void
	activeTabIndex?: number
	tabMobileView?: 'stack' | 'scroll'
	tabColorStyles?: Record<string, Color>
}

const TabbedContentBlock = ({
	className = '',
	children,
	populateOverviewTab = false,
	overviewTabTitle = 'Overview',

	activeTabIndex = 0,
	onChange = () => undefined,
	tabMobileView = 'stack',
	tabColorStyles = {},
}: TabbedContentBlockProps): JSX.Element => {
	const leftArrowRef = useRef<HTMLDivElement>(null)
	const rightArrowRef = useRef<HTMLDivElement>(null)
	const [currentTab, setCurrentTab] = useState(activeTabIndex)
	const [expandedTab, setExpandedTab] = useState<number | null>(0)
	const [isDesktop, setIsDesktop] = useState(false)

	useEffect((): (() => void) => {
		const handleResize = (): void => {
			setIsDesktop(window.innerWidth >= 1024)
		}
		handleResize()
		window.addEventListener('resize', handleResize)
		return (): void => {
			window.removeEventListener('resize', handleResize)
		}
	}, [])

	const effectiveTabMobileView = isDesktop ? 'scroll' : tabMobileView
	const isTabScrollable = effectiveTabMobileView === 'scroll'
	const isTabStacked = effectiveTabMobileView === 'stack'

	const handleOnChange = (index: number | string): void => {
		setCurrentTab(Number(index))
		if (isTabStacked) {
			setExpandedTab(expandedTab === Number(index) ? null : Number(index))
		}
		onChange(index)
	}

	const getTitleWithIcon = (
		icon: React.ReactNode | null,
		title: React.ReactNode,
		index: number
	): JSX.Element => {
		const getStyle = (): Record<string, string> => ({
			transform: expandedTab === index ? 'rotate(45deg)' : 'rotate(0)',
		})

		return (
			<>
				{isTabStacked ? (
					<svg
						className="mr-4 h-5 w-5 fill-current transition-transform duration-300"
						height="26.883"
						style={getStyle()}
						viewBox="0 0 26.883 26.883"
						width="26.883"
					>
						<path
							d="M1.9,18.729l-1.6-1.595a1,1,0,0,1,0-1.414l6.2-6.2-6.2-6.2a1,1,0,0,1,0-1.414L1.9.306a1,1,0,0,1,1.415,0l6.2,6.2,6.2-6.2a1,1,0,0,1,1.415,0l1.595,1.6a1,1,0,0,1,0,1.414l-6.2,6.2,6.2,6.2a1,1,0,0,1,0,1.414l-1.595,1.595a1,1,0,0,1-1.415,0l-6.2-6.2-6.2,6.2a1,1,0,0,1-1.414,0Z"
							transform="translate(13.442 -0.018) rotate(45)"
						/>
					</svg>
				) : null}
				{!isTabStacked &&
					isValidElement(icon) &&
					cloneElement(
						icon as React.ReactElement<{className: string}>,
						{
							className: 'mr-4 shrink-0',
						}
					)}
				<p>{title}</p>
			</>
		)
	}

	// Dynamically create an overview tab using the content from other tabs passed from the module call
	const getOverviewTab = (): React.ReactElement<TabProps> => {
		return (
			<Tab title={overviewTabTitle}>
				{children.map(
					(tab, index) =>
						isValidElement(tab) && (
							<div key={tab.key}>
								<h3 className={index > 0 ? 'mt-6' : ''}>
									{tab.props.title}
								</h3>
								{Children.toArray(
									tab.props.children as React.ReactNode
								).map((child) => child)}
							</div>
						)
				)}
			</Tab>
		)
	}

	// Getting tabs content to pass to <Tabs> as data
	const getTabs = (): (TabItem & {
		key: string
		tabClassNames: Record<string, string>
		disableOverflow?: boolean
		icon?: React.ReactNode
		title: React.ReactNode
	})[] => {
		// Append the generated overview tab content based on the populateOverviewTab property
		const clonedChildren = populateOverviewTab
			? [getOverviewTab()].concat(children as JSX.Element[])
			: children

		return clonedChildren.map((tab, idx) => {
			const colorChoice = tab.props.color?.choice as keyof Color
			const colorStyles = tabColorStyles[colorChoice] || {
				defaultStyle: 'bg-gray-400 text-gray-900 hover:bg-gray-300',
				collapsed: 'bg-gray-300 text-blue-900',
				selected: 'bg-gray-200 text-blue-900',
			}

			const tabClassNames = {
				default: `${colorStyles.defaultStyle} text-sm py-4 px-6 flex-grow flex-1 border-l-2 border-gray-500 cursor-pointer flex items-center`,
				collapsed: `${colorStyles.collapsed} border-l-0 border-b`,
				selected: `${colorStyles.selected} font-bold`,
			}

			return {
				key: String(tab.key ?? `tab-${idx}`),
				title: tab.props.title,
				getContent: () => tab.props.children,
				tabClassNames,
				panelClassName: tab.props.disableOverflow
					? styles.panelNoOverflow
					: styles.panel,
				disableOverflow: tab.props.disableOverflow,
				icon: tab.props.icon,
			}
		})
	}

	useEffect(() => {
		if (!isTabScrollable) return

		const tabContainer = document.getElementsByClassName(
			'RRT__tabs'
		)[0] as HTMLElement
		const checkScrollPosition = (): void => {
			const {scrollLeft, clientWidth, scrollWidth} = tabContainer
			if (scrollLeft === 0) {
				leftArrowRef.current?.classList.add('hidden')
			}
			if (scrollLeft !== 0 && leftArrowRef.current) {
				leftArrowRef.current.classList.remove('hidden')
				leftArrowRef.current.style.background =
					'linear-gradient(to right, #FFFFFF 75%, transparent)'
			}
			if (scrollWidth > clientWidth && rightArrowRef.current) {
				rightArrowRef.current.classList.remove('hidden')
				rightArrowRef.current.style.background =
					'linear-gradient(to left, #FFFFFF 75%, transparent)'
			}
			if (Math.ceil(scrollLeft) + clientWidth >= scrollWidth) {
				rightArrowRef.current?.classList.add('hidden')
			}
		}

		// Initial check
		checkScrollPosition()

		// Add scroll event listener
		tabContainer.addEventListener('scroll', checkScrollPosition)

		// Cleanup event listener on component unmount
		return () => {
			tabContainer.removeEventListener('scroll', checkScrollPosition)
		}
	}, [isTabScrollable, currentTab])

	const onRightArrowClicked = (): void => {
		const tabs = document.getElementsByClassName(
			'RRT__tabs'
		)[0] as HTMLElement
		tabs.scrollLeft += 100
	}

	const onLeftArrowClicked = (): void => {
		const tabs = document.getElementsByClassName(
			'RRT__tabs'
		)[0] as HTMLElement
		tabs.scrollLeft -= 100
	}

	const tabs = getTabs()

	return (
		<div className="relative">
			{isTabScrollable ? (
				<>
					<div
						className="absolute left-0 top-2 z-10 h-9 w-10"
						ref={leftArrowRef}
					>
						<EllipseButton
							className="ml-2 mt-2"
							icon={ButtonIcons.ArrowLeft}
							iconSize="w-3 h-3"
							onClick={onLeftArrowClicked}
							size="w-4 h-4"
						/>
					</div>
					<div
						className="absolute right-0 top-2 z-10 h-9 w-10"
						ref={rightArrowRef}
					>
						<EllipseButton
							className="ml-4 mt-2"
							icon={ButtonIcons.ArrowRight}
							iconSize="w-3 h-3"
							onClick={onRightArrowClicked}
							size="w-4 h-4"
						/>
					</div>
				</>
			) : null}

			{isTabScrollable ? (
				<Tabs
					containerClass={`${className} overflow-hidden w-full container-wide shadow-secondary rounded-lg flex flex-col`}
					items={tabs.map((tab, index) => ({
						title: tab.title,
						getContent: tab.getContent,
						tabClassName: `${tab.tabClassNames.default} ${
							index === currentTab
								? tab.tabClassNames.selected
								: ''
						} ${
							index !== currentTab
								? tab.tabClassNames.collapsed
								: ''
						}`,
						panelClassName: tab.panelClassName,
					}))}
					name="TabbedContentBlock"
					onChange={handleOnChange}
					selected={currentTab}
					showMore={false}
					tabsWrapperClass="flex border-b border-gray-300 overflow-x-auto whitespace-nowrap no-scrollbar"
					transform={false}
				/>
			) : (
				<section className={styles.tabContent}>
					{tabs.map((tab, index) => (
						<div className="border-b border-gray-400" key={tab.key}>
							<button
								className={`${tab.tabClassNames.default} ${
									expandedTab === index
										? tab.tabClassNames.selected
										: tab.tabClassNames.collapsed
								} w-full text-left`}
								onClick={() => {
									handleOnChange(index)
								}}
								type="button"
							>
								{getTitleWithIcon(tab.icon, tab.title, index)}
							</button>
							{expandedTab === index && (
								<div className={tab.panelClassName}>
									{tab.getContent?.()}
								</div>
							)}
						</div>
					))}
				</section>
			)}
		</div>
	)
}

export {TabbedContentBlock, Tab as ContentBlockTab}
